import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

export interface AssignVehicleParams {
  shiftId: string
  vehicleId: string
  // The route writes vehicle_id only, so the plate for the toast comes from the caller.
  registrationDisplay: string
}

export interface UnassignVehicleParams {
  shiftId: string
  vehicleId: string
}

export interface AssignmentResult {
  shiftId: string
  vehicleId: string | null
}

// supabase.functions.invoke cannot address a sub-route, so this goes through fetch with a manual
// bearer token, as useQueryPeople does for /people/query.
async function postToShifts(route: string, body: unknown): Promise<AssignmentResult> {
  const { data: sessionData } = await supabase.auth.getSession()
  const token = sessionData?.session?.access_token
  if (!token) throw new Error("No authentication token available")

  const response = await fetch(`${getFunctionsBaseUrl()}/shifts/${route}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })

  const payload = await response.json().catch(() => ({}))

  // A rejected criterion arrives as the top-level `error`, which is the message worth showing.
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || payload.message || `Request failed: ${response.statusText}`)
  }

  return payload.data as AssignmentResult
}

function useAssignmentInvalidation() {
  const queryClient = useQueryClient()

  return (vehicleId: string) => {
    queryClient.invalidateQueries({ queryKey: ["vehicles"] })
    queryClient.invalidateQueries({ queryKey: ["vehicle-shifts", vehicleId] })
    queryClient.invalidateQueries({ queryKey: ["shifts"] })
    // Every block's shortlist, not just this one's: the vehicle is now taken for overlapping blocks.
    queryClient.invalidateQueries({ queryKey: ["candidate-vehicles"] })
  }
}

export function useAssignVehicle() {
  const invalidate = useAssignmentInvalidation()

  return useMutation({
    mutationFn: ({ shiftId, vehicleId }: AssignVehicleParams) =>
      postToShifts("assign-vehicle", { shiftId, vehicleId }),
    onSuccess: (_result, variables) => {
      invalidate(variables.vehicleId)
      toast.success("Vehicle assigned", {
        description: `${variables.registrationDisplay} is now on this block.`,
      })
    },
    onError: (error: Error) => {
      toast.error("Could not assign vehicle", { description: error.message })
    },
  })
}

export function useUnassignVehicle() {
  const invalidate = useAssignmentInvalidation()

  return useMutation({
    mutationFn: ({ shiftId }: UnassignVehicleParams) =>
      postToShifts("unassign-vehicle", { shiftId }),
    onSuccess: (_result, variables) => {
      invalidate(variables.vehicleId)
      toast.success("Vehicle removed", { description: "This block no longer has a vehicle." })
    },
    onError: (error: Error) => {
      toast.error("Could not remove vehicle", { description: error.message })
    },
  })
}
